import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAccount } from 'wagmi';
import { useContract, PageData } from '../hooks/useContract';
import { BookPage } from '../components/BookPage';
import { UnlockModal } from '../components/UnlockModal';

export function BookView() {
  const { address: paramAddress } = useParams<{ address?: string }>();
  const { address: connectedAddress, isConnected } = useAccount();
  const { getUserPages, getPage, unlockWithPayment, unlockWithPassword } = useContract();
  const [pageIds, setPageIds] = useState<number[]>([]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [currentPageData, setCurrentPageData] = useState<PageData | null>(null);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Determine which address book to load
  const displayAddress = paramAddress || connectedAddress;

  useEffect(() => {
    // Need to be connected to view any book
    if (!isConnected || !connectedAddress) {
      navigate('/');
      return;
    }
    loadBook();
  }, [displayAddress, isConnected, connectedAddress]);

  useEffect(() => {
    if (pageIds.length > 0) {
      loadCurrentPage();
    }
  }, [currentPageIndex, pageIds]);

  const loadBook = async () => {
    if (!displayAddress) return;
    setLoading(true);
    const ids = await getUserPages(displayAddress);
    setPageIds(ids);
    setLoading(false);
  };

  const loadCurrentPage = async () => {
    if (pageIds.length === 0) return;
    const pageId = pageIds[currentPageIndex];
    const page = await getPage(pageId);
    setCurrentPageData(page);
  };

  const handlePrev = () => {
    if (currentPageIndex > 0) {
      setCurrentPageIndex(currentPageIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentPageIndex < pageIds.length - 1) {
      setCurrentPageIndex(currentPageIndex + 1);
    }
  };

  const handleDone = () => {
    navigate('/profile');
  };

  const handleUnlock = () => {
    setShowUnlockModal(true);
  };

  const handleUnlockSuccess = async () => {
    setShowUnlockModal(false);
    await loadCurrentPage();
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <div className="spinner"></div>
      </div>
    );
  }

  if (pageIds.length === 0) {
    return (
      <div className="page-container" style={{ textAlign: 'center' }}>
        <h2 style={{ fontFamily: 'Press Start 2P', color: '#f7931a', marginBottom: '20px' }}>
          Your ₿ook is Empty
        </h2>
        <p style={{ color: '#d4a574', marginBottom: '30px' }}>
          Create your first secret page to start your book!
        </p>
        <button className="btn btn-primary" onClick={() => navigate('/create')}>
          Create First Page
        </button>
      </div>
    );
  }

  if (!currentPageData) {
    return (
      <div className="loading-spinner">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <>
      <BookPage
        pageNumber={currentPageIndex + 1}
        totalPages={pageIds.length}
        content={currentPageData.decryptedContent || currentPageData.encryptedContent}
        isLocked={!currentPageData.hasAccess}
        onPrev={handlePrev}
        onNext={handleNext}
        onDone={handleDone}
        onUnlock={currentPageData.hasAccess ? undefined : handleUnlock}
      />

      {showUnlockModal && currentPageData && (
        <UnlockModal
          pageId={currentPageData.id}
          unlockPrice={currentPageData.unlockPrice}
          hasPassword={currentPageData.hasPassword}
          onUnlockWithPayment={async (id) => {
            const success = await unlockWithPayment(id, currentPageData.unlockPrice);
            if (success) handleUnlockSuccess();
            return success;
          }}
          onUnlockWithPassword={async (id, password) => {
            const success = await unlockWithPassword(id, password);
            if (success) handleUnlockSuccess();
            return success;
          }}
          onClose={() => setShowUnlockModal(false)}
        />
      )}
    </>
  );
}
